import PropTypes from 'prop-types';
import { useState, useCallback } from 'react';
import { Upload, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
// Correction de l'accept pour react-dropzone v11+
const ACCEPTED_FILE_TYPES = {
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'application/pdf': ['.pdf'],
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'video/webm': ['.webm'],
};

export default function FileUploader({ onFilesChange }) {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);

  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      setError(null);

      if (fileRejections.length > 0) {
        const errors = fileRejections.map(({ file, errors }) =>
          errors.map((err) =>
            err.code === 'file-too-large'
              ? `${file.name} dépasse la taille maximale de 5MB.`
              : `${file.name} n'est pas un format supporté.`
          )
        );
        setError(errors.flat().join(', '));
        return;
      }

      const newFiles = [...files, ...acceptedFiles];
      setFiles(newFiles);
      onFilesChange?.(newFiles);
    },
    [files, onFilesChange]
  );

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    onFilesChange?.(newFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
  });

  return (
    <div style={{ marginBottom: '1rem' }}>
      <div
        {...getRootProps()}
        style={{
          border: '2px dashed gray',
          borderRadius: '8px',
          padding: '1rem',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive ? '#f0f8ff' : '#fff',
        }}
      >
        <input {...getInputProps()} />
        <Upload style={{ margin: '0 auto', width: '48px', height: '48px', color: 'gray' }} />
        <p>
          {isDragActive
            ? 'Déposez les fichiers ici...'
            : 'Glissez-déposez vos fichiers ou cliquez pour sélectionner'}
        </p>
        <p style={{ fontSize: '12px', color: 'gray' }}>
          Formats acceptés : JPG, PNG, GIF, WebP, PDF, MP4, MOV, WebM - Max 5MB
        </p>
      </div>

      {error && (
        <p style={{ color: 'red', marginTop: '0.5rem' }}>
          {error}
        </p>
      )}

      {files.length > 0 && (
        <ul style={{ marginTop: '1rem', listStyle: 'none', padding: '0' }}>
          {files.map((file, index) => (
            <li
              key={file.name + index}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.5rem',
                backgroundColor: '#f9f9f9',
                borderRadius: '4px',
                marginBottom: '0.5rem',
              }}
            >
              <span style={{ fontSize: '14px', color: '#333', flex: '1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </span>
              <button
                onClick={() => removeFile(index)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'gray',
                  cursor: 'pointer',
                }}
              >
                <X style={{ width: '16px', height: '16px', color: 'red' }} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

FileUploader.propTypes = {
  onFilesChange: PropTypes.func,
};
